import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchHipsProperties, normalizeHipsUrl } from './HipsProperties'
import styles from './style.module.scss'

type Props = {
  baseUrl?: string
  onCommit: (baseUrl?: string) => void
}

export function HipsUrlEditor({ baseUrl, onCommit }: Props) {
  const [input, setInput] = useState(baseUrl ?? '')
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(false)
  const generation = useRef(0)
  const pendingUrl = useRef<string | undefined>(undefined)

  useEffect(() => {
    ++generation.current
    pendingUrl.current = undefined
    setInput(baseUrl ?? '')
    setError(undefined)
    setLoading(false)
  }, [baseUrl])

  useEffect(() => () => { ++generation.current }, [])

  const commit = useCallback(async () => {
    const trimmed = input.trim()
    if (!trimmed) {
      ++generation.current
      setError(undefined)
      setLoading(false)
      if (baseUrl !== undefined) onCommit(undefined)
      return
    }

    const normalized = normalizeHipsUrl(input)
    if (!normalized) {
      ++generation.current
      setError('http(s) の HiPS URL を入力してください。')
      setLoading(false)
      return
    }

    if (normalized === baseUrl) {
      setInput(normalized)
      setError(undefined)
      return
    }

    if (pendingUrl.current === normalized) return
    const id = ++generation.current
    pendingUrl.current = normalized
    setError(undefined)
    setLoading(true)

    try {
      await fetchHipsProperties(normalized)
      if (id !== generation.current) return
      onCommit(normalized)
    }
    catch (error) {
      if (id !== generation.current) return
      setError(`HiPS 情報の取得に失敗しました: ${String(error)}`)
    }
    finally {
      if (id === generation.current) {
        pendingUrl.current = undefined
        setLoading(false)
      }
    }
  }, [baseUrl, input, onCommit])

  return (
    <form
      className={styles.hipsUrlEditor}
      onSubmit={event => {
        event.preventDefault()
        void commit()
      }}
    >
      <label htmlFor="hips-base-url">URL</label>
      <input
        id="hips-base-url"
        aria-label="HiPS URL"
        type="text"
        spellCheck={false}
        className={styles.hipsUrlInput}
        value={input}
        onChange={event => {
          ++generation.current
          pendingUrl.current = undefined
          setLoading(false)
          setError(undefined)
          setInput(event.currentTarget.value)
        }}
        onBlur={() => { void commit() }}
      />
      {loading && <span className={styles.hipsUrlStatus}>確認中…</span>}
      {error && <div className={styles.hipsUrlError} role="alert">{error}</div>}
    </form>
  )
}
